import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardMedia,
  Typography,
  Grid,
  Avatar,
  Chip,
  Container,
  Stack,
  Button,
  Divider,
  Paper,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Building2Icon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LayersIcon from "@mui/icons-material/Layers";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CategoryIcon from "@mui/icons-material/Category";
import LaunchIcon from "@mui/icons-material/Launch";
import axios from "axios";
import { toast } from "react-toastify";

import useCommonGetInit from "../../hooks/useCommonGetInit";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import MuPb from "../../widgets/MuPb";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import { ProductCategoriesView } from "../../widgets/ProductCategoriesView";
import { getErrorResponse, toastError } from "../../utils/util";
import { getHeader } from "../../utils/utils2";

const ExhibitorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useCommonGetInit(
    `${API_ENDPOINT}/user/a/get-exhibitor?id=${id}`
  );
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      </Container>
    );
  }

  const {
    user_id,
    username,
    companyName,
    contactSalutation,
    contactFirstName,
    contactLastName,
    contactDesignation,
    mobile,
    companyEmail,
    contactEmail,
    address1,
    address2,
    address3,
    country,
    state,
    city,
    pin,
    phone,
    fax,
    website,
    companyShortName,
    fascia,
    weAre,
    industrySector,
    exhibitorCategory,
    productCategories,
    hallNumber,
    standNumber,
    spaceType,
    exhibitArrival,
    exhibitArrivalTimeFrom,
    exhibitArrivalTimeTo,
    materialArrival,
    materialArrivalTimeFrom,
    materialArrivalTimeTo,
    dismantling,
    dismantlingTimeFrom,
    dismantlingTimeTo,
    standSize,
    profile_image,
    profile_banner,
  } = data || {};

  const displayVal = (val) => (val !== null && val !== undefined && val !== "" ? val : "N/A");

  const companyTitle = displayVal(companyName?.value);
  const contactName = `${displayVal(contactSalutation?.value)} ${displayVal(contactFirstName?.value)} ${displayVal(contactLastName?.value)}`
    .replace(/N\/A/g, "")
    .trim() || "N/A";

  const rawHall = (hallNumber?.value || "").trim();
  const rawStand = (standNumber?.value || "").trim();
  const hallFormatted = rawHall ? (rawHall.toLowerCase().startsWith("hall") ? rawHall : `Hall ${rawHall}`) : "";
  const standFormatted = rawStand ? (rawStand.toLowerCase().startsWith("stand") ? rawStand : `Stand ${rawStand}`) : "";
  const floorAlloc = hallFormatted && standFormatted ? `${hallFormatted} • ${standFormatted}` : hallFormatted || standFormatted || "Not Allocated";

  const fullAddress = [
    address1?.value,
    address2?.value,
    address3?.value,
    city?.value?.name,
    state?.value?.name,
    country?.value?.name,
    pin?.value ? `PIN: ${pin.value}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Box sx={{ pb: 8 }}>
      <SimpleYesNoDialog
        title="Confirm Exhibitor Deletion"
        message={`Are you sure you want to delete exhibitor "${companyTitle}"? This action cannot be undone.`}
        onYes={async () => {
          setDeleting(true);
          setShowDelete(false);
          try {
            const { data } = await axios.delete(
              `${API_ENDPOINT}/admin/delete-exhibitor?id=${id}`,
              getHeader()
            );
            toast.success(data.message || "Exhibitor deleted successfully");
            setTimeout(() => {
              navigate("/exhibitors-list");
            }, 300);
          } catch (err) {
            toastError(err);
          } finally {
            setDeleting(false);
          }
        }}
        show={showDelete}
        setShow={setShowDelete}
      />

      <Container maxWidth="lg">
        {/* Navigation Action Bar */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#475569",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 2,
              py: 0.75,
              backgroundColor: "#FFFFFF",
              border: "1px solid #CBD5E1",
              "&:hover": { backgroundColor: "#F1F5F9" },
            }}
          >
            Back to Directory
          </Button>

          <Stack direction="row" spacing={1.5}>
            <LoadingButton
              size="small"
              loading={deleting}
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setShowDelete(true)}
              sx={{ borderRadius: "10px", fontWeight: 600, textTransform: "none", py: 0.75, px: 2 }}
            >
              Delete Exhibitor
            </LoadingButton>

            <Button
              component={Link}
              to={`/edit-exhibitor-form/${id}`}
              variant="contained"
              startIcon={<EditIcon fontSize="small" />}
              sx={{
                borderRadius: "10px",
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
                fontWeight: 600,
                textTransform: "none",
                py: 0.75,
                px: 2.5,
                boxShadow: "0px 4px 12px rgba(37, 99, 235, 0.25)",
                "&:hover": { backgroundColor: "#1D4ED8" },
              }}
            >
              Edit Exhibitor
            </Button>
          </Stack>
        </Stack>

        {/* Hero Banner Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: "20px",
            border: "1px solid #E2E8F0",
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 6px 24px rgba(15, 23, 42, 0.06)",
            overflow: "hidden",
            mb: 3.5,
          }}
        >
          {/* Banner Image or Gradient */}
          {profile_banner ? (
            <CardMedia component="img" height="220" image={profile_banner} alt={companyTitle} />
          ) : (
            <Box
              sx={{
                height: 180,
                background: "linear-gradient(135deg, #1E293B 0%, #2563EB 50%, #3B82F6 100%)",
                position: "relative",
              }}
            />
          )}

          <Box sx={{ px: { xs: 2.5, md: 4 }, pb: 3, pt: 0, position: "relative" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "center", sm: "flex-start" }}
              justifyContent="space-between"
              spacing={2.5}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "center", sm: "flex-start" }}
                spacing={2.5}
                flex={1}
              >
                <Avatar
                  src={profile_image || ""}
                  alt={companyTitle}
                  sx={{
                    width: 110,
                    height: 110,
                    mt: "-55px",
                    border: "4px solid #FFFFFF",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                    backgroundColor: "#EEF4FF",
                    color: "#2563EB",
                    fontSize: "2.5rem",
                    fontWeight: 800,
                  }}
                >
                  {companyTitle ? companyTitle.charAt(0).toUpperCase() : "C"}
                </Avatar>

                <Box flex={1} sx={{ pt: { xs: 0, sm: 1.5 }, textAlign: { xs: "center", sm: "left" } }}>
                  <Typography variant="h4" fontWeight="800" color="#1E293B" sx={{ letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                    {companyTitle}
                  </Typography>
                  {fascia?.value && (
                    <Typography variant="body2" color="#64748B" fontWeight="600" mt={0.5}>
                      Fascia Name: <span style={{ color: "#334155" }}>{fascia.value}</span>
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Chip
                icon={<LayersIcon style={{ color: "#2563EB" }} />}
                label={floorAlloc}
                sx={{
                  mt: { xs: 0, sm: 1.5 },
                  backgroundColor: "#EEF4FF",
                  color: "#2563EB",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  py: 2.2,
                  px: 1.5,
                  borderRadius: "12px",
                  border: "1px solid #C7D9FE",
                }}
              />
            </Stack>
          </Box>
        </Card>

        {/* Content Details Grid */}
        <Grid container spacing={3}>
          {/* Left Column: Contact & Address */}
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              {/* Primary Contact Person Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#EEF4FF",
                      color: "#2563EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon />
                  </Box>
                  <Typography variant="h6" fontWeight="700" color="#1E293B">
                    Contact Person Information
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Contact Name
                    </Typography>
                    <Typography variant="body1" fontWeight="700" color="#1E293B" mt={0.25}>
                      {contactName}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Designation
                    </Typography>
                    <Typography variant="body1" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(contactDesignation?.value)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Mobile Number
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(mobile?.value)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Office Phone
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(phone?.value)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Contact Email
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#2563EB" mt={0.25}>
                      {displayVal(contactEmail?.value)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Company Email
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#2563EB" mt={0.25}>
                      {displayVal(companyEmail?.value)}
                    </Typography>
                  </Grid>

                  {website?.value && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                        Website
                      </Typography>
                      <Box mt={0.25}>
                        <a
                          href={website.value.startsWith("http") ? website.value : `https://${website.value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#2563EB",
                            fontWeight: 700,
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {website.value} <LaunchIcon style={{ fontSize: 14 }} />
                        </a>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Company Address Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#F0FDF4",
                      color: "#16A34A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LocationOnIcon />
                  </Box>
                  <Typography variant="h6" fontWeight="700" color="#1E293B">
                    Company Location & Address
                  </Typography>
                </Stack>

                <Typography variant="body1" color="#334155" fontWeight="500" sx={{ lineHeight: 1.6 }}>
                  {fullAddress || "N/A"}
                </Typography>

                {country?.value?.flag && (
                  <Box mt={1.5} display="flex" alignItems="center" gap={1}>
                    <img
                      src={country.value.flag}
                      alt={`${country.value.name} flag`}
                      style={{ width: "28px", borderRadius: "3px", boxShadow: "0px 1px 4px rgba(0,0,0,0.1)" }}
                    />
                    <Typography variant="body2" fontWeight="600" color="#64748B">
                      {country.value.name}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Product Categories Section */}
              {productCategories?.value && productCategories.value.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: "#FEF3C7",
                        color: "#D97706",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CategoryIcon />
                    </Box>
                    <Typography variant="h6" fontWeight="700" color="#1E293B">
                      Product Categories
                    </Typography>
                  </Stack>

                  <ProductCategoriesView items={productCategories.value} />
                </Paper>
              )}
            </Stack>
          </Grid>

          {/* Right Column: Exhibition Allocation & Logistics */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              {/* Stand & Booth Allocation Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#EEF4FF",
                      color: "#2563EB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Building2Icon />
                  </Box>
                  <Typography variant="h6" fontWeight="700" color="#1E293B">
                    Exhibition Stand Allocation
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #F1F5F9" }}>
                      <Typography variant="caption" color="#64748B" fontWeight="600">
                        Hall Number
                      </Typography>
                      <Typography variant="h6" fontWeight="700" color="#2563EB" mt={0.25}>
                        {displayVal(hallNumber?.value)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #F1F5F9" }}>
                      <Typography variant="caption" color="#64748B" fontWeight="600">
                        Stand Number
                      </Typography>
                      <Typography variant="h6" fontWeight="700" color="#2563EB" mt={0.25}>
                        {displayVal(standNumber?.value)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Space Type
                    </Typography>
                    <Typography variant="body2" fontWeight="700" color="#1E293B" mt={0.25}>
                      {displayVal(spaceType?.value)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Stand Size
                    </Typography>
                    <Typography variant="body2" fontWeight="700" color="#1E293B" mt={0.25}>
                      {displayVal(standSize?.value)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="#64748B" fontWeight="600" display="block">
                      Exhibitor Category
                    </Typography>
                    <Typography variant="body2" fontWeight="600" color="#334155" mt={0.25}>
                      {displayVal(exhibitorCategory?.value)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Logistics & Timings Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: "#F3E8FF",
                      color: "#7C3AED",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccessTimeIcon />
                  </Box>
                  <Typography variant="h6" fontWeight="700" color="#1E293B">
                    Logistics & Arrival Timings
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  {/* Exhibit Arrival */}
                  <Box sx={{ p: 1.5, backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #F1F5F9" }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#1E293B">
                      Exhibit Arrival
                    </Typography>
                    <Typography variant="caption" color="#64748B" display="block">
                      Date: {exhibitArrival?.value ? new Date(exhibitArrival.value).toLocaleDateString() : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="#64748B" display="block">
                      Time Slot: {displayVal(exhibitArrivalTimeFrom?.value)} - {displayVal(exhibitArrivalTimeTo?.value)}
                    </Typography>
                  </Box>

                  {/* Material Arrival */}
                  <Box sx={{ p: 1.5, backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #F1F5F9" }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#1E293B">
                      Material Arrival
                    </Typography>
                    <Typography variant="caption" color="#64748B" display="block">
                      Date: {materialArrival?.value ? new Date(materialArrival.value).toLocaleDateString() : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="#64748B" display="block">
                      Time Slot: {displayVal(materialArrivalTimeFrom?.value)} - {displayVal(materialArrivalTimeTo?.value)}
                    </Typography>
                  </Box>

                  {/* Dismantling */}
                  <Box sx={{ p: 1.5, backgroundColor: "#F8FAFC", borderRadius: "10px", border: "1px solid #F1F5F9" }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#1E293B">
                      Dismantling Schedule
                    </Typography>
                    <Typography variant="caption" color="#64748B" display="block">
                      Date: {dismantling?.value ? new Date(dismantling.value).toLocaleDateString() : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="#64748B" display="block">
                      Time Slot: {displayVal(dismantlingTimeFrom?.value)} - {displayVal(dismantlingTimeTo?.value)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Industry Sector Tags Card */}
              {industrySector?.value && industrySector.value.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #E2E8F0",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.03)",
                  }}
                >
                  <Typography variant="h6" fontWeight="700" color="#1E293B" mb={1.5}>
                    Industry Sectors
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {industrySector.value.map((sector, idx) => (
                      <Chip
                        key={idx}
                        label={sector}
                        sx={{
                          backgroundColor: "#EEF4FF",
                          color: "#2563EB",
                          fontWeight: 700,
                          fontSize: "0.775rem",
                          borderRadius: "8px",
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ExhibitorDetail;
